import React, { useState, useEffect } from "react";
import { Card, Form, Button, Row, Col, Alert, Modal } from "react-bootstrap";
import {
  FaUser,
  FaLock,
  FaTrash,
  FaQuestionCircle,
  FaBox,
  FaChartBar,
  FaPlus,
  FaTrash as FaTrashIcon,
  FaEdit,
  FaCheck,
  FaArrowRight,
  FaEye,
  FaCopy,
  FaGripVertical,
} from "react-icons/fa";
import { MdOutlinePayment } from "react-icons/md";
import {
  updateUserDetails,
  updateUserPassword,
  deleteEvent,
  deleteStudent,
  deleteGroup,
  submitEvaluationTemplate,
  updateDefaultTemplate,
  updateTemplateArchiveStatus,
  suspendUserAccount,
  changePackagePlan,
} from "../../../../backend/firebaseFunctions";
import "./SettingsPane.css";
import Joyride, { STATUS } from "react-joyride";
import CustomModal from "../../../Global/Modal/CustomModal";
// Drag and drop
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
// Payment Components
import { PaymentSettings } from "../../Payment Components";
import PackageChangeModal from "../../Payment Components/PackageChangeModal";

const SettingsPane = ({
  userData,
  activateAlert,
  userID,
  setUserData,
  groups,
  students,
  events,
  setGroups,
  setStudents,
  setEvents,
  coaches,
}) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [formData, setFormData] = useState({
    name: userData?.userName || "",
    email: userData?.userEmailAddress || "",
    phone: userData?.userPhoneNumber || "",
    organizationName: userData?.userOrganizationName || "",
    newPassword: "",
    confirmPassword: "",
    paymentProvider: userData?.paymentProvider || "iCredit",
    paymentApiKey: userData?.paymentApiKey || "",
  });

  // Track original form data for change detection
  const [originalFormData, setOriginalFormData] = useState({ ...formData });

  // Track if form has been modified
  const [isFormModified, setIsFormModified] = useState(false);

  // Track if form is being submitted
  const [isSubmitting, setIsSubmitting] = useState(false);

  // For data loss prevention
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState(null);

  // Data wipe states
  const [wipeConfirmationStep, setWipeConfirmationStep] = useState(0);
  const [wipeConfirmationText, setWipeConfirmationText] = useState("");
  const [showWipeModal, setShowWipeModal] = useState(false);
  const [wipeDataTypes, setWipeDataTypes] = useState({
    pastEvents: false,
    futureEvents: false,
    selectedGroups: [],
  });

  // Tour state
  const [runTour, setRunTour] = useState(false);

  // Suspend account modal state
  const [showSuspendModal, setShowSuspendModal] = useState(false);

  // Package change modal state
  const [showPackageChangeModal, setShowPackageChangeModal] = useState(false);
  const [isChangingPackage, setIsChangingPackage] = useState(false);

  // Tour button style
  const tourButtonStyle = {
    position: "absolute",
    top: "20px",
    left: "20px",
    backgroundColor: "#0066CC",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  };

  // Define tour steps
  const steps = [
    {
      target: ".settings-pane",
      content:
        "ברוכים הבאים למסך ההגדרות! כאן תוכלו לנהל את כל ההגדרות של החשבון שלכם. בואו נעשה סיור קצר!",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".settings-nav",
      content: "כאן תוכלו לנווט בין קטגוריות ההגדרות השונות.",
      placement: "right",
    },
    {
      target: ".settings-nav-item:nth-child(1)",
      content:
        "בקטגוריית הפרטים האישיים תוכלו לעדכן את המידע הבסיסי של החשבון שלכם.",
      placement: "right",
    },
    {
      target: ".settings-nav-item:nth-child(2)",
      content:
        "בקטגוריית פרטי תוכנית השימוש תוכלו לראות את פרטי החבילה שלכם, סטטוס התשלום ותקופת הניסיון.",
      placement: "right",
    },
    {
      target: ".settings-nav-item:nth-child(3)",
      content: "בקטגוריית איפוס סיסמא תוכלו לשנות את הסיסמה של החשבון שלכם.",
      placement: "right",
    },
    {
      target: ".settings-nav-item:nth-child(4)",
      content: "בקטגוריית מחיקת נתונים תוכלו למחוק נתונים מהמערכת בצורה בטוחה.",
      placement: "right",
    },
    {
      target: ".settings-guide-button",
      content: "אם תרצו לראות את הסיור שוב, תוכלו ללחוץ על כפתור זה בכל עת!",
      placement: "left",
      spotlightClicks: true,
    },
  ];

  // Handle tour events
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  const handleTourButtonClick = () => {
    setRunTour(true);
  };

  // Update original form data when userData changes
  useEffect(() => {
    const newFormData = {
      name: userData?.userName || "",
      email: userData?.userEmailAddress || "",
      phone: userData?.userPhoneNumber || "",
      organizationName: userData?.userOrganizationName || "",
      newPassword: "",
      confirmPassword: "",
      paymentProvider: userData?.paymentProvider || "iCredit",
      paymentApiKey: userData?.paymentApiKey || "",
    };

    setFormData(newFormData);
    setOriginalFormData(newFormData);
    setIsFormModified(false);
  }, [userData]);

  // Check for form modifications
  useEffect(() => {
    const hasChanges =
      formData.name !== originalFormData.name ||
      formData.email !== originalFormData.email ||
      formData.phone !== originalFormData.phone ||
      formData.organizationName !== originalFormData.organizationName ||
      formData.paymentProvider !== originalFormData.paymentProvider ||
      formData.paymentApiKey !== originalFormData.paymentApiKey;

    setIsFormModified(hasChanges);
  }, [formData, originalFormData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        notifications: {
          ...formData.notifications,
          [name]: checked,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormModified) return;

    setIsSubmitting(true);

    try {
      // Check if email has changed to determine if we need to update auth user
      const emailChanged = formData.email !== originalFormData.email;

      await updateUserDetails(
        userID,
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          organizationName: formData.organizationName,
          paymentProvider: formData.paymentProvider,
          paymentApiKey: formData.paymentApiKey,
        },
        emailChanged
      ); // Pass true if email has changed

      // Update original form data to match current form data
      setOriginalFormData({ ...formData });

      // Update the userData prop if it's provided
      if (setUserData) {
        setUserData({
          ...userData,
          userName: formData.name,
          userEmailAddress: formData.email,
          userPhoneNumber: formData.phone,
          userOrganizationName: formData.organizationName,
          paymentProvider: formData.paymentProvider,
          paymentApiKey: formData.paymentApiKey,
        });
      }

      setIsFormModified(false);

      activateAlert("success", "הגדרות עודכנו", "ההגדרות שלך עודכנו בהצלחה");
    } catch (error) {
      console.error("Error updating user details:", error);

      // Show more specific error message for auth-related errors
      if (error.code === "auth/requires-recent-login") {
        activateAlert(
          "danger",
          "שגיאה",
          "עדכון האימייל דורש התחברות מחדש. אנא התנתק והתחבר שוב."
        );
      } else {
        activateAlert(
          "danger",
          "שגיאה",
          "אירעה שגיאה בעדכון ההגדרות. אנא נסה שוב."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSecuritySubmit = async (e) => {
    e.preventDefault();

    // Check if password fields are filled
    if (!formData.newPassword || !formData.confirmPassword) {
      activateAlert("warning", "שדות חסרים", "נא למלא את כל שדות הסיסמה");
      return;
    }

    // Validate that new password and confirmation match
    if (formData.newPassword !== formData.confirmPassword) {
      activateAlert(
        "warning",
        "סיסמאות לא תואמות",
        "הסיסמה החדשה ואימות הסיסמה אינם תואמים"
      );
      return;
    }

    // Validate password strength (optional but recommended)
    if (formData.newPassword.length < 6) {
      activateAlert(
        "warning",
        "סיסמה חלשה",
        "הסיסמה החדשה חייבת להכיל לפחות 6 תווים"
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Update password without current password verification
      await updateUserPassword(userID, formData.newPassword);

      // Clear password fields after successful update
      setFormData({
        ...formData,
        newPassword: "",
        confirmPassword: "",
      });

      activateAlert("success", "הסיסמה עודכנה", "הסיסמה שלך עודכנה בהצלחה");
    } catch (error) {
      console.error("Error updating user password:", error);

      // Provide more specific error messages
      if (error.code === "auth/weak-password") {
        activateAlert("danger", "סיסמה חלשה", "הסיסמה החדשה חלשה מדי");
      } else {
        activateAlert(
          "danger",
          "שגיאה",
          "אירעה שגיאה בעדכון הסיסמה. אנא נסה שוב."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTabChange = (tab) => {
    if (isFormModified) {
      setPendingTabChange(tab);
      setShowUnsavedChangesModal(true);
    } else {
      setActiveTab(tab);
    }
  };

  const handleConfirmTabChange = () => {
    setActiveTab(pendingTabChange);
    setShowUnsavedChangesModal(false);
    setPendingTabChange(null);
  };

  const handleCancelTabChange = () => {
    setShowUnsavedChangesModal(false);
    setPendingTabChange(null);
  };

  const handleDiscardChanges = () => {
    setFormData({ ...originalFormData });
    setIsFormModified(false);
    setShowUnsavedChangesModal(false);
    setPendingTabChange(null);
  };

  const handleWipeData = async () => {
    if (wipeConfirmationStep < 2) {
      setWipeConfirmationStep(wipeConfirmationStep + 1);
      return;
    }

    // Final verification
    if (wipeConfirmationText !== "אני מבין, מחק את המידע שבחרתי") {
      activateAlert("warning", "אימות נדרש", "נא להזין את המשפט המדויק לאימות");
      return;
    }

    try {
      // Delete past events
      // Note: Real-time listeners will automatically update the state, so we don't need to manually update
      if (wipeDataTypes.pastEvents && events) {
        const pastEvents = (events || []).filter((event) => {
          if (!event || !event.date) return false;
          // Handle Firebase Timestamp object with toDate() method
          const eventDate = event.date?.toDate?.()?.getTime() ||
            (event.date.seconds ? event.date.seconds * 1000 + (event.date.nanoseconds || 0) / 1e6 : 0);
          return eventDate < Date.now();
        });

        for (const event of pastEvents) {
          await deleteEvent(userID, event.id);
        }
        // Real-time listener will automatically update events state
      }

      // Delete future events
      // Note: Real-time listeners will automatically update the state, so we don't need to manually update
      if (wipeDataTypes.futureEvents && events) {
        const futureEvents = (events || []).filter((event) => {
          if (!event || !event.date) return false;
          // Handle Firebase Timestamp object with toDate() method
          const eventDate = event.date?.toDate?.()?.getTime() ||
            (event.date.seconds ? event.date.seconds * 1000 + (event.date.nanoseconds || 0) / 1e6 : 0);
          return eventDate >= Date.now();
        });

        for (const event of futureEvents) {
          await deleteEvent(userID, event.id);
        }
        // Real-time listener will automatically update events state
      }

      // Delete selected groups and their students
      // Note: Real-time listeners will automatically update the state, so we don't need to manually update
      if (wipeDataTypes.selectedGroups.length > 0) {
        // First, get all students in the selected groups
        const studentsToDelete = (students || []).filter(
          (student) => {
            if (!student.groupsIDs) {
              return false;
            }

            if (Array.isArray(student.groupsIDs)) {
              return student.groupsIDs.some(groupId => wipeDataTypes.selectedGroups.includes(groupId));
            } else {
              return wipeDataTypes.selectedGroups.includes(student.groupsIDs);
            }
          }
        );

        // Delete all students in the selected groups
        for (const student of studentsToDelete) {
          await deleteStudent(userID, student.id);
        }

        // Delete the groups
        for (const groupId of wipeDataTypes.selectedGroups) {
          await deleteGroup(userID, groupId);
        }
        // Real-time listeners will automatically update groups and students state
      }

      activateAlert("success", "נתונים נמחקו", "הנתונים נמחקו בהצלחה");
      setWipeConfirmationStep(0);
      setWipeConfirmationText("");
      setShowWipeModal(false);
      setWipeDataTypes({
        pastEvents: false,
        futureEvents: false,
        selectedGroups: [],
      });
    } catch (error) {
      console.error("Error during data wipe:", error);
      activateAlert(
        "error",
        "שגיאה",
        "אירעה שגיאה במחיקת הנתונים. אנא נסה שוב."
      );
    }
  };

  const handleGroupSelection = (groupId) => {
    setWipeDataTypes((prev) => ({
      ...prev,
      selectedGroups: prev.selectedGroups.includes(groupId)
        ? prev.selectedGroups.filter((id) => id !== groupId)
        : [...prev.selectedGroups, groupId],
    }));
  };

  // Calculate remaining days since last payment
  const getRemainingDays = () => {
    const paymentDetails = userData?.paymentDetails || {};
    if (!paymentDetails.lastPaymentDate) return 0;

    const lastPaymentDate = new Date(paymentDetails.lastPaymentDate.seconds * 1000);
    const currentDate = new Date();
    const daysSinceLastPayment = Math.floor((currentDate - lastPaymentDate) / (1000 * 60 * 60 * 24));
    const remainingDays = Math.max(0, 30 - daysSinceLastPayment);

    return remainingDays;
  };

  const remainingDays = getRemainingDays();

  const handleSuspendAccountClick = () => {
    setShowSuspendModal(true);
  };

  const handleSuspendAccount = async () => {
    try {
      // Call the Firebase function to suspend the account
      const result = await suspendUserAccount(userID);

      // Update the userData prop if it's provided
      if (setUserData) {
        setUserData(result.userData);
      }

      activateAlert("success", "חשבון הושהה", "החשבון שלך הושהה בהצלחה");
      setShowSuspendModal(false);
    } catch (error) {
      console.error("Error suspending account:", error);
      activateAlert("danger", "שגיאה", "אירעה שגיאה בהשעיית החשבון. אנא נסה שוב.");
    }
  };

  const handlePackageChangeClick = () => {
    setShowPackageChangeModal(true);
  };

  const handlePackageChange = async (newPackage) => {
    setIsChangingPackage(true);
    try {
      const result = await changePackagePlan(userID, newPackage.name);

      // Update user data with new package
      if (setUserData && result.success) {
        const updatedUserData = {
          ...userData,
          paymentDetails: {
            ...userData.paymentDetails,
            plan: newPackage.name,
          },
        };
        setUserData(updatedUserData);
      }

      activateAlert("success", "תוכנית שונתה", "התוכנית שונתה בהצלחה");
      setShowPackageChangeModal(false);
    } catch (error) {
      console.error("Package change failed:", error);
      activateAlert("danger", "שגיאה", "אירעה שגיאה בשינוי התוכנית. אנא נסה שוב.");
    } finally {
      setIsChangingPackage(false);
    }
  };

  const renderProfileSettings = () => {
    return (
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>שם מלא</Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="הכנס את שמך המלא"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>אימייל</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="הכנס את כתובת האימייל שלך"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>טלפון</Form.Label>
          <Form.Control
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="הכנס את מספר הטלפון שלך"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>שם הארגון</Form.Label>
          <Form.Control
            type="text"
            name="organizationName"
            value={formData.organizationName}
            onChange={handleInputChange}
            placeholder="הכנס את שם הארגון"
            disabled={userData?.userType === "coach"}
          />
        </Form.Group>

        <Button
          variant={isFormModified ? "success" : "secondary"}
          type="submit"
          disabled={!isFormModified || isSubmitting}
        >
          {isSubmitting ? "שומר..." : "שמור שינויים"}
        </Button>
      </Form>
    );
  };

  const renderSecuritySettings = () => {
    // Check if both password fields are filled
    const isPasswordFormValid =
      formData.newPassword.trim() !== "" &&
      formData.confirmPassword.trim() !== "";

    return (
      <Form onSubmit={handleSecuritySubmit}>
        <Form.Group className="mb-3">
          <Form.Label>סיסמה חדשה</Form.Label>
          <Form.Control
            type="password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleInputChange}
            placeholder="הכנס סיסמה חדשה"
            required
            minLength={6}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>אימות סיסמה חדשה</Form.Label>
          <Form.Control
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder="אימות סיסמה חדשה"
            required
          />
        </Form.Group>

        <Button
          variant={isPasswordFormValid ? "primary" : "secondary"}
          type="submit"
          disabled={isSubmitting || !isPasswordFormValid}
          className={!isPasswordFormValid ? "opacity-50" : ""}
        >
          {isSubmitting ? "מעדכן..." : "עדכן סיסמה"}
        </Button>
      </Form>
    );
  };

  const renderPackageDetails = () => {
    return (
      <PaymentSettings
        userData={userData}
        onSuspendAccountClick={handleSuspendAccountClick}
        onPackageChangeClick={handlePackageChangeClick}
        userID={userID}
        groups={groups}
        coaches={coaches}
        onUserDataUpdate={setUserData}
        isChangingPackage={isChangingPackage}
      />
    );
  };

  const renderDataWipeSettings = () => {
    return (
      <div>
        <Alert variant="danger" className="mb-4">
          <h4>תהליך מחיקת הנתונים</h4>
          <p>
            תהליך מחיקת הנתונים מאפשר לך למחוק מידע רב מהארגון שלך לצמיתות.
            לרוב, תהליך זה יהיה שימושי במידה ויש מידע ישן שמחיקה ידנית שלו תיקח
            זמן רב.
          </p>
          <strong>
            התהליך מורכב ממספר שלבים בשביל לסייע לך לבצע את פעולת המחיקה בצורה
            בטוחה ללא טעויות, אך חשוב לקרוא בקפדנות את ההוראות.
          </strong>
        </Alert>

        <Form>
          <Form.Group className="mb-4">
            <Form.Label>בחר קבוצות למחיקה:</Form.Label>
            <Form.Text className="text-muted mb-3">
              הקבוצות שייבחרו יימחקו מהמערכת והספורטאים שמתאמנים בהן יימחקו גם
              הם מהמערכת לצמיתות
            </Form.Text>
            <div className="ms-3">
              {groups && groups.length > 0 ? (
                groups.map((group) => (
                  <Form.Check
                    key={group.id}
                    type="checkbox"
                    label={`${group.name}, סניף ${group.dojo}`}
                    checked={wipeDataTypes.selectedGroups.includes(group.id)}
                    onChange={() => handleGroupSelection(group.id)}
                  />
                ))
              ) : (
                <p className="text-muted">אין קבוצות זמינות</p>
              )}
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>בחר נתונים נוספים למחיקה:</Form.Label>
            <div className="ms-3">
              <Form.Check
                type="checkbox"
                label="אירועים עתידיים"
                checked={wipeDataTypes.futureEvents}
                onChange={(e) =>
                  setWipeDataTypes({
                    ...wipeDataTypes,
                    futureEvents: e.target.checked,
                  })
                }
              />
              <Form.Check
                type="checkbox"
                label="אירועים מהעבר"
                checked={wipeDataTypes.pastEvents}
                onChange={(e) =>
                  setWipeDataTypes({
                    ...wipeDataTypes,
                    pastEvents: e.target.checked,
                  })
                }
              />
            </div>
          </Form.Group>

          <Button
            variant="danger"
            onClick={() => setShowWipeModal(true)}
            disabled={
              !wipeDataTypes.pastEvents &&
              !wipeDataTypes.futureEvents &&
              wipeDataTypes.selectedGroups.length === 0
            }
          >
            התחל תהליך מחיקה
          </Button>
        </Form>

        <Modal
          show={showWipeModal}
          onHide={() => {
            setShowWipeModal(false);
            setWipeConfirmationStep(0);
            setWipeConfirmationText("");
          }}
          dir="rtl"
        >
          <Modal.Header>
            <Modal.Title>אימות מחיקת נתונים</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {wipeConfirmationStep === 0 && (
              <div>
                <strong>האם אתה בטוח שברצונך למחוק את הנתונים שבחרת?</strong>
                <p>
                  פעולה זו אינה הפיכה. הנתונים הבאים יימחקו מהמערכת לצמיתות:
                </p>
                <ul>
                  {wipeDataTypes.pastEvents && (
                    <li>
                      {
                        (events || []).filter((event) => {
                          if (!event || !event.date) return false;
                          // Handle Firebase Timestamp object with toDate() method
                          const eventDate = event.date?.toDate?.()?.getTime() ||
                            (event.date.seconds ? event.date.seconds * 1000 + (event.date.nanoseconds || 0) / 1e6 : 0);
                          return eventDate < Date.now();
                        }).length
                      }{" "}
                      אירועים מהעבר יימחקו
                    </li>
                  )}
                  {wipeDataTypes.futureEvents && (
                    <li>
                      {
                        (events || []).filter((event) => {
                          if (!event || !event.date) return false;
                          // Handle Firebase Timestamp object with toDate() method
                          const eventDate = event.date?.toDate?.()?.getTime() ||
                            (event.date.seconds ? event.date.seconds * 1000 + (event.date.nanoseconds || 0) / 1e6 : 0);
                          return eventDate >= Date.now();
                        }).length
                      }{" "}
                      אירועים עתידיים יימחקו
                    </li>
                  )}
                  {wipeDataTypes.selectedGroups.length > 0 && (
                    <>
                      <li>
                        {wipeDataTypes.selectedGroups.length} קבוצות יימחקו,
                        כולל הספורטאים שמתאמנים בהן:
                      </li>
                      <ul>
                        {(groups || [])
                          .filter((group) =>
                            group && group.id && wipeDataTypes.selectedGroups.includes(group.id)
                          )
                          .map((group) => {
                            // Ensure we're using the latest students prop and properly matching IDs
                            // groupsIDs is typically an array, but handle both cases
                            const groupIdStr = String(group.id);
                            const studentsInGroup = (students || []).filter((student) => {
                              if (!student || !student.groupsIDs) return false;
                              // groupsIDs is usually an array, but handle both array and single value
                              if (Array.isArray(student.groupsIDs)) {
                                return student.groupsIDs.some(id => String(id) === groupIdStr);
                              }
                              return String(student.groupsIDs) === groupIdStr;
                            }).length;
                            return (
                              <li key={group.id}>
                                {group.name} מסניף {group.dojo} (
                                {studentsInGroup} ספורטאים)
                              </li>
                            );
                          })}
                      </ul>
                    </>
                  )}
                </ul>
              </div>
            )}
            {wipeConfirmationStep === 1 && (
              <div>
                <div className="alert alert-danger">
                  <strong>אזהרה: פעולה זו תמחק את הנתונים לצמיתות.</strong>
                  <br />
                  <strong>
                    הנתונים לא יישמרו יותר במערכת ולא יהיה ניתן לגשת אליהם, גם
                    לא יהיה ניתן לשחזר אותם במידת הצורך.
                  </strong>
                </div>

                <div className="mt-4">
                  <h6>נתונים שיימחקו:</h6>
                  <ul className="list-group mt-3" style={{ padding: "0px" }}>
                    {wipeDataTypes.pastEvents && (
                      <li className="list-group-item">
                        <h6 className="mb-2">
                          <strong>אירועים מהעבר:</strong>
                        </h6>
                        <ul className="list-unstyled ms-3">
                          {(events || [])
                            .filter((event) => {
                              if (!event || !event.date) return false;
                              // Handle Firebase Timestamp object with toDate() method
                              const eventDate = event.date?.toDate?.()?.getTime() ||
                                (event.date.seconds ? event.date.seconds * 1000 + (event.date.nanoseconds || 0) / 1e6 : 0);
                              return eventDate < Date.now();
                            })
                            .map((event) => {
                              // Get the date for display
                              const eventDateObj = event.date?.toDate ? event.date.toDate() :
                                (event.date.seconds ? new Date(event.date.seconds * 1000 + (event.date.nanoseconds || 0) / 1e6) : new Date());
                              return (
                                <li key={event.id} className="mb-1">
                                  • {event.name} -{" "}
                                  {eventDateObj.toLocaleDateString("he-IL")}
                                </li>
                              );
                            })}
                        </ul>
                      </li>
                    )}

                    {wipeDataTypes.futureEvents && (
                      <li className="list-group-item">
                        <h6 className="mb-2">
                          <strong>אירועים עתידיים:</strong>
                        </h6>
                        <ul className="list-unstyled ms-3">
                          {(events || [])
                            .filter((event) => {
                              if (!event || !event.date) return false;
                              // Handle Firebase Timestamp object with toDate() method
                              const eventDate = event.date?.toDate?.()?.getTime() ||
                                (event.date.seconds ? event.date.seconds * 1000 + (event.date.nanoseconds || 0) / 1e6 : 0);
                              return eventDate >= Date.now();
                            })
                            .map((event) => {
                              // Get the date for display
                              const eventDateObj = event.date?.toDate ? event.date.toDate() :
                                (event.date.seconds ? new Date(event.date.seconds * 1000 + (event.date.nanoseconds || 0) / 1e6) : new Date());
                              return (
                                <li key={event.id} className="mb-1">
                                  • {event.name} -{" "}
                                  {eventDateObj.toLocaleDateString("he-IL")}
                                </li>
                              );
                            })}
                        </ul>
                      </li>
                    )}

                    {wipeDataTypes.selectedGroups.length > 0 && (
                      <li className="list-group-item">
                        <h6 className="mb-2">
                          <strong>קבוצות וספורטאים:</strong>
                        </h6>
                        <ul className="list-unstyled ms-3">
                          {(groups || [])
                            .filter((group) =>
                              group && group.id && wipeDataTypes.selectedGroups.includes(group.id)
                            )
                            .map((group) => {
                              // Ensure we're using the latest students prop and properly matching IDs
                              // groupsIDs is typically an array, but handle both cases
                              const groupIdStr = String(group.id);
                              const groupStudents = (students || []).filter((student) => {
                                if (!student || !student.groupsIDs) return false;
                                // groupsIDs is usually an array, but handle both array and single value
                                if (Array.isArray(student.groupsIDs)) {
                                  return student.groupsIDs.some(id => String(id) === groupIdStr);
                                }
                                return String(student.groupsIDs) === groupIdStr;
                              });
                              return (
                                <li key={group.id} className="mb-3">
                                  <li>
                                    • {group.name} - סניף {group.dojo}
                                  </li>
                                  <ul className="list-unstyled ms-4 mt-1">
                                    {groupStudents.length > 0 ? (
                                      groupStudents.map((student) => (
                                        <li key={student.id}>
                                          {student.name}, ת"ז {student.IDnumber}
                                          {Array.isArray(student.groupsIDs) && student.groupsIDs.length > 1 && (
                                            <div style={{ color: 'red', fontWeight: 'bold', fontSize: '0.9em', marginTop: '2px' }}>
                                              ספורטאי זה משוייך לקבוצות נוספות
                                            </div>
                                          )}
                                        </li>
                                      ))
                                    ) : (
                                      <li className="text-muted">אין ספורטאים בקבוצה זו</li>
                                    )}
                                  </ul>
                                </li>
                              );
                            })}
                        </ul>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="alert alert-warning mt-4">
                  <p className="mb-1">אם אינך בטוח, צור קשר עם התמיכה הטכנית</p>
                  <p className="mb-0">האם אתה בטוח שברצונך להמשיך?</p>
                </div>
              </div>
            )}
            {wipeConfirmationStep === 2 && (
              <div>
                <p>לצורך אימות סופי לביצוע הפעולה, הזן את המשפט הבא במדויק:</p>
                <p className="text-danger">"אני מבין, מחק את המידע שבחרתי"</p>
                <Form.Control
                  type="text"
                  value={wipeConfirmationText}
                  onChange={(e) => setWipeConfirmationText(e.target.value)}
                  placeholder="הזן את המשפט"
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowWipeModal(false);
                setWipeConfirmationStep(0);
                setWipeConfirmationText("");
              }}
            >
              ביטול
            </Button>
            <Button
              variant="danger"
              onClick={handleWipeData}
              disabled={
                wipeConfirmationStep === 2 &&
                wipeConfirmationText !== "אני מבין, מחק את המידע שבחרתי"
              }
            >
              {wipeConfirmationStep < 2 ? "המשך" : "מחק נתונים"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  return (
    <>
      <div className="settings-pane">
        <Joyride
          steps={steps}
          run={runTour}
          continuous
          showProgress
          showSkipButton
          disableScrolling={true}
          callback={handleJoyrideCallback}
          styles={{
            options: {
              arrowColor: "#fff",
              backgroundColor: "#fff",
              overlayColor: "rgba(0, 0, 0, 0.5)",
              primaryColor: "#0066CC",
              textColor: "#333",
              zIndex: 1000,
            },
            tooltipContainer: {
              direction: "rtl",
              textAlign: "right",
            },
            buttonNext: {
              backgroundColor: "#0066CC",
              fontSize: 14,
              padding: "8px 16px",
            },
            buttonBack: {
              marginRight: 10,
              fontSize: 14,
              padding: "8px 16px",
            },
            buttonSkip: {
              color: "#666",
              fontSize: 14,
              padding: "8px 16px",
            },
          }}
          locale={{
            back: "הקודם",
            close: "סגור",
            last: "סיום",
            next: "הבא",
            skip: "דלג",
          }}
        />
        <button
          style={tourButtonStyle}
          onClick={handleTourButtonClick}
          title="הפעל סיור מודרך"
          className="settings-guide-button"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
          }}
        >
          <FaQuestionCircle size={20} />
        </button>
        <h2 className="mb-4">הגדרות</h2>

        <Row>
          <Col md={3}>
            <Card className="settings-nav">
              <Card.Body className="p-0">
                <div
                  className={`settings-nav-item ${activeTab === "profile" ? "active" : ""
                    }`}
                  onClick={() => handleTabChange("profile")}
                >
                  <FaUser className="me-2" />
                  פרטים אישיים
                </div>
                <div
                  className={`settings-nav-item ${activeTab === "package" ? "active" : ""
                    }`}
                  onClick={() => handleTabChange("package")}
                >
                  <MdOutlinePayment className="me-2" />
                  פרטי תוכנית השימוש
                </div>
                <div
                  className={`settings-nav-item ${activeTab === "security" ? "active" : ""
                    }`}
                  onClick={() => handleTabChange("security")}
                >
                  <FaLock className="me-2" />
                  איפוס סיסמא
                </div>
                <div
                  className={`settings-nav-item ${activeTab === "dataWipe" ? "active" : ""
                    }`}
                  onClick={() => handleTabChange("dataWipe")}
                >
                  <FaTrash className="me-2" />
                  מחיקת נתונים
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={9}>
            <Card>
              <Card.Body>
                {activeTab === "profile" && renderProfileSettings()}
                {activeTab === "security" && renderSecuritySettings()}
                {activeTab === "package" && renderPackageDetails()}
                {activeTab === "dataWipe" && renderDataWipeSettings()}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Suspend Account Modal */}
      <CustomModal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        width={40}
        height={60}
        title="השעיית חשבון"
        footerButtons={[
          {
            title: "השעה חשבון",
            style: "red",
            onClick: handleSuspendAccount,
          },
          {
            title: "ביטול",
            style: "grey",
            onClick: () => setShowSuspendModal(false),
          },
        ]}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div style={{ marginBottom: "20px" }}>
            <p style={{ fontSize: "16px", lineHeight: "1.6", direction: "rtl" }}>
              השעיית החשבון תאפשר לך להשתמש במערכת עד <strong>30 ימים</strong> מתאריך התשלום האחרון שלך.
            </p>
            <p style={{ fontSize: "16px", lineHeight: "1.6", direction: "rtl" }}>
              השעיית החשבון תפסיק את המנוי לחבילה שלך ותפסיק את חיובי האשראי.
            </p>
          </div>

          <div style={{
            backgroundColor: "#f8f9fa",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "20px"
          }}>
            <h5 style={{ marginBottom: "10px", direction: "rtl" }}>ימים נותרים לשימוש:</h5>
            <span
              className="badge p-3"
              style={{
                fontSize: "18px",
                backgroundColor: remainingDays > 10 ? "#28a745" : remainingDays > 5 ? "#ffc107" : "#dc3545",
                color: "#FFFFFF",
                direction: "rtl"
              }}
            >
              {remainingDays} ימים
            </span>
          </div>

          <div style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "20px"
          }}>
            <p style={{ margin: "0", fontSize: "14px", color: "#856404", direction: "rtl" }}>
              <strong>שים לב:</strong> המידע שנאסף בחשבון שלך יישמר במערכת עד שנה מיום השהיית החשבון. במידה ותתחרט, תוכל להפעיל את החשבון מחדש.
            </p>
          </div>
        </div>
      </CustomModal>

      {/* Package Change Modal */}
      <PackageChangeModal
        isOpen={showPackageChangeModal}
        onClose={() => setShowPackageChangeModal(false)}
        userData={userData}
        groups={groups}
        coaches={coaches}
        onPackageChange={handlePackageChange}
      />
    </>
  );
};

// Modern wrapper and style injection for SettingsPane
const ModernizedSettingsPane = (props) => (
  <div className="settings-pane-bg">
    <div className="settings-pane-container">
      <SettingsPane {...props} />
    </div>
    <style>{`
      .settings-pane-bg {
        min-height: 100vh;
        background: linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%);
        padding: 32px 0;
      }
      .settings-pane-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 16px;
      }
      .settings-pane h2 {
        font-weight: 800;
        font-size: 2.5rem;
        letter-spacing: 1px;
        margin-bottom: 24px;
        text-align: center;
      }
      .settings-nav {
        border-radius: 16px !important;
        box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
        background: #fff !important;
        border: none !important;
        margin-bottom: 24px;
      }
      .settings-nav-item {
        padding: 18px 24px;
        font-size: 1.2rem;
        font-weight: 600;
        color: #444;
        border-radius: 12px;
        margin: 8px 12px;
        cursor: pointer;
        transition: background 0.15s, color 0.15s;
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .settings-nav-item.active, .settings-nav-item:hover {
        background: #e3e9f7;
        color: #1976d2;
      }
      .settings-pane .card {
        border-radius: 18px !important;
        box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
        background: #fff !important;
        border: none !important;
        margin-bottom: 32px;
      }
      .settings-pane .btn, .settings-pane button {
        border-radius: 999px !important;
        font-weight: 700 !important;
        font-size: 1.1rem !important;
        padding: 12px 32px !important;
        box-shadow: 0 2px 8px rgba(0,102,204,0.10) !important;
        transition: background 0.2s, box-shadow 0.2s;
      }
      .settings-pane .btn-primary, .settings-pane .btn-success {
        background: #0066CC !important;
        color: #fff !important;
        border: none !important;
      }
      .settings-pane .btn-primary:hover, .settings-pane .btn-success:hover {
        background: #0052a3 !important;
        box-shadow: 0 4px 16px #0066cc33 !important;
      }
      .settings-pane .btn-danger {
        background: #d32f2f !important;
        color: #fff !important;
        border: none !important;
      }
      .settings-pane .btn-danger:hover {
        background: #b71c1c !important;
      }
      .settings-pane .form-control, .settings-pane .form-select {
        border-radius: 10px !important;
        box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
        border: 1px solid #e0e0e0 !important;
        background: #fff !important;
        font-size: 1.1rem !important;
        padding: 12px 18px !important;
        margin-bottom: 12px;
      }
      .settings-pane .form-label {
        font-weight: 700;
        font-size: 1.1rem;
        margin-bottom: 6px;
      }
      .settings-pane .alert {
        border-radius: 12px !important;
        font-size: 1.1rem;
        padding: 18px 24px !important;
      }
      .settings-pane .modal-content {
        border-radius: 18px !important;
        box-shadow: 0 2px 12px rgba(0,0,0,0.10) !important;
      }
             .settings-pane .modal-header, .settings-pane .modal-footer {
         border: none !important;
       }
      .settings-pane .badge {
        border-radius: 8px !important;
        font-size: 1.1rem !important;
        padding: 8px 16px !important;
      }
      .settings-pane .d-flex.gap-2 > * {
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
      .settings-guide-button {
        position: absolute;
        top: 20px;
        left: 20px;
        background-color: #0066CC;
        color: white;
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        z-index: 100;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      }
      @media (max-width: 991px) {
        .settings-pane-container {
          max-width: 100% !important;
          padding: 0 4px !important;
        }
        .settings-pane h2 {
          font-size: 2rem;
        }
      }
    `}</style>
  </div>
);

export default ModernizedSettingsPane;
